/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequesterReferralReasonProps {
  id: string;
  name: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RequesterReferralReasonRecord extends Record<RequesterReferralReasonProps>({
  id: null as any,
  name: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RequesterReferralReasonRecord {
    if (json == null) {
      throw new Error('RequesterReferralReasonRecord.fromJS: parameter cannot be null');
    }
    return new RequesterReferralReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequesterReferralReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'RequesterReferralReasonJson.name'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RequesterReferralReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('RequesterReferralReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequesterReferralReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequesterReferralReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'RequesterReferralReasonJson.name'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RequesterReferralReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('RequesterReferralReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequesterReferralReasonViewModelBase implements RequesterReferralReasonProps {
  protected readonly _props: RequesterReferralReasonProps;
  private _json?: RequesterReferralReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: RequesterReferralReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRequesterReferralReasonEntities = (state: AppState) =>
  state.entityStores.requesterReferralReason.entities;

export const selectRequesterReferralReasonById = (id: string) => (state: AppState) =>
  state.entityStores.requesterReferralReason.entities.get(id);

export const selectRequesterReferralReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.requesterReferralReason.meta.get(id);

export const selectRequesterReferralReasonAndMetadataById = (id: string) =>
  createSelector(
    selectRequesterReferralReasonById(id),
    selectRequesterReferralReasonMetadataById(id),
    (requesterReferralReason, meta) => ({ requesterReferralReason, meta }),
  );

export function selectRequesterReferralReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRequesterReferralReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RequesterReferralReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRequesterReferralReasonEntitiesByName(name: string) {
  return createSelector(selectAllRequesterReferralReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}
//#endregion Selectors
